import React, { useCallback, useState, type ReactNode } from 'react';
import OldButton from '@atlaskit/button';
import Button from '@atlaskit/button/new';
import UploadIcon from '@atlaskit/icon/glyph/upload';
import { Box, Stack, xcss } from '@atlaskit/primitives';
import Tooltip from '@atlaskit/tooltip';
import { useIntl } from '@atlassian/jira-intl';
import {
	fireTrackAnalytics,
	fireUIAnalytics,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import {
	CONFIG_FILE_UPLOAD_TYPE,
	CONFIG_MAX_SIZE_BYTES,
	LEARN_ABOUT_USING_CONFIGURATION_FILES_LINK,
} from '../../../common/constants';
import { Accordion } from '../../../common/ui/accordion';
import { UploadButton } from '../../../common/ui/upload-button';
import {
	useMigrationId,
	useProjectData,
	useSelectedSourceItem,
	useUploadedConfigName,
	useUploadedFileName,
} from '../../../controllers/import-state';
import { useUploadConfiguration } from '../../../services/upload-configuration';
import messages from './messages';
import { validateFile, transformToValidProjectData } from './utils';

type Props = {
	setIsUploading: (isDisable: boolean) => void;
};

export const UploadConfiguration = ({ setIsUploading }: Props) => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [uploadedFileName] = useUploadedFileName();
	const [uploadedConfigName, { setUploadedConfigName }] = useUploadedConfigName();
	const [migrationId] = useMigrationId();
	const [showFormatError, setShowFormatError] = useState(false);
	const [, { setProjectData, resetProjectData }] = useProjectData();
	const { isFileUploading, hasErrorOccurred, uploadConfiguration, abort } =
		useUploadConfiguration();
	const [selectedSourceItem] = useSelectedSourceItem();
	const projectSettings = selectedSourceItem?.[selectedSourceItem?.supportedFlow];

	const onReceiveFile = useCallback(
		async (file: File) => {
			validateFile(file).then((data) => {
				setIsUploading(true);
				if (data === null) {
					setShowFormatError(true);
					return;
				}
				fireUIAnalytics(createAnalyticsEvent({}), 'configFile uploading', { fileSize: file.size });
				migrationId &&
					uploadConfiguration(file, migrationId).then((isUploaded) => {
						if (isUploaded) {
							setUploadedConfigName(file.name);
							fireTrackAnalytics(createAnalyticsEvent({}), 'configFile uploaded', {
								fileSize: file.size,
							});
							setProjectData(transformToValidProjectData(data, projectSettings || []));
						}
						setIsUploading(false);
					});
			});
		},
		[
			createAnalyticsEvent,
			migrationId,
			projectSettings,
			setIsUploading,
			setProjectData,
			setUploadedConfigName,
			uploadConfiguration,
		],
	);

	const onCancel = useCallback(() => {
		setUploadedConfigName(null);
		abort();
		resetProjectData();
		setIsUploading(false);
		showFormatError && setShowFormatError(false);
	}, [abort, resetProjectData, setIsUploading, setUploadedConfigName, showFormatError]);

	return (
		<Box xcss={wrapperStyles}>
			<Accordion heading={messages.uploadConfigurationAccordionHeading} closedAppearance="subtle">
				<Stack space="space.200">
					<Box as="p">
						{formatMessage(messages.uploadConfigurationDescription, {
							link: (content: ReactNode) => (
								<OldButton
									appearance="link"
									spacing="none"
									target="_blank"
									href={LEARN_ABOUT_USING_CONFIGURATION_FILES_LINK}
								>
									{content}
								</OldButton>
							),
						})}
					</Box>
					{uploadedFileName ? (
						<UploadButton
							uploadText={formatMessage(messages.uploadConfiguration)}
							acceptType={CONFIG_FILE_UPLOAD_TYPE}
							onReceiveFile={onReceiveFile}
							onCancel={onCancel}
							fileSizeLimit={CONFIG_MAX_SIZE_BYTES}
							isFileUploading={isFileUploading}
							hasErrorOccured={hasErrorOccurred}
							fileName={uploadedConfigName}
							formatError={showFormatError ? formatMessage(messages.formatError) : null}
						/>
					) : (
						<Tooltip content={formatMessage(messages.disabledTooltip)} position="auto">
							{(tooltipProps) => (
								<Box>
									<Button iconBefore={UploadIcon} isDisabled {...tooltipProps}>
										{formatMessage(messages.uploadConfiguration)}
									</Button>
								</Box>
							)}
						</Tooltip>
					)}
				</Stack>
			</Accordion>
		</Box>
	);
};

const wrapperStyles = xcss({
	paddingBlock: 'space.400',
});
